@use 'style/utils';
@use './shared' as shared;

$CLASS: '.joy-drawer';

$_mobile-breakpoint: 767px;
$_mobile-cta_breakpoint: 400px;

@keyframes drawerTransitionStart {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

:host {
  /**
   * @prop --drawer-width: Override default container width
   * @prop --drawer-padding: Override default container padding (one value for the four sides)
   */
  --drawer-width: 580px;
  --drawer-transition-duration: var(--joy-transition-duration-default);

  display: block;
  position: fixed;
  opacity: 0;
  width: 0;
  height: 0;
  z-index: -1;
  top: 0;
  left: 0;
  right: 0;
  padding: 0;
  margin: 0 auto;
  box-sizing: border-box;
  outline: none;
  text-align: left;
  margin-left: auto;
}

:host([elevated][open]) {
  width: var(--drawer-width);
  left: unset;
}

:host([size="large"]) {
  --drawer-width: 780px;
}


@media screen and (max-width: #{$_mobile-breakpoint}) {
  :host {
    padding: 0;
    --drawer-width: 100% !important;
  }
}

:host([open]) {
  opacity: 1;
  height: 100%;
  z-index: var(--joy-core-z-index-dialog);
  width: 100%;
}

#{$CLASS}__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  background-color: var(--joy-color-overlay);

  &--open {
    width: 100%;
    height: 100%;
  }

  &___disabled {
    cursor: not-allowed;
  }
}

#{$CLASS} {
  $_padding: var(--drawer-padding, var(--joy-core-spacing-8));
  $_dimension: calc(100% - #{$_padding});
  $full-height: calc(100vh - #{$_padding});

  position: relative;
  display: flex;
  flex-direction: column;
  background-color: white;
  width: var(--drawer-width);
  max-width: 100%;
  margin: 0 auto;
  border-radius: var(--joy-core-radius-4);
  padding: $_padding;
  box-shadow: var(--joy-core-elevation-4);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  max-height: calc(100vh - 100px);

  &--wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0px;
    width: fit-content;

    @media screen and (max-width: #{$_mobile-breakpoint}) {
      width: 100%;
    }

    &, #{$CLASS} {
      height: 100%;
      max-height: 100%;
      left: unset;
    }
  
    #{$CLASS} {
      display: flex;
      flex-direction: column;
      border-radius: var(--joy-core-radius-4);
  
      &--body {
        flex-grow: 1;
        overflow-y: auto;
        overflow-x: visible;
      }
    }
  
    #{$CLASS} {
      border-radius: var(--joy-core-radius-4) 0 0 var(--joy-core-radius-4);
      margin: 0 0 0 auto;

      @media screen and (max-width: #{$_mobile-breakpoint}) {
        border-radius: var(--joy-core-radius-0);
      }
    }

    @keyframes rightDrawerTransitionStart {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }

    @keyframes rightDrawerTransitionEnd {
      from {
        transform: translateX(0);
      }
      to {
        transform: translateX(100%);
      }
    }

    &#{$CLASS}--wrapper-open {
      animation: rightDrawerTransitionStart var(--drawer-transition-duration) var(--joy-transition-timing-function) forwards;
    }

    &#{$CLASS}--wrapper-hiding {
      animation: rightDrawerTransitionEnd var(--drawer-transition-duration) var(--joy-transition-timing-function) forwards;
    }
  }

  @media screen and (max-width: #{$_mobile-breakpoint}) {
    padding: var(--drawer-padding, var(--joy-core-spacing-6));
    height: 100vh;
    max-height: 100vh;
    max-width: 100%;
    overflow-y: auto;
    border-radius: var(--joy-core-radius-0);
  }

  &--header {
    margin-bottom: var(--joy-core-spacing-7);

    &__title {
      padding-right: 45px;
      @include shared.headerTitleStyle();
    }
  }

  &--subheader {
    position: relative;
    @include utils.getFontProperties(primary-300);
    margin: var(--joy-core-spacing-2) 0 0 0;
    padding: 0;
  }

  &--body {
    font-size: var(--joy-font-size-primary-400);
  }

  // The id is used only to have more weight that default joy-icon css
  #joy-drawer--close {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    cursor: pointer;
    right: var(--joy-core-spacing-6);
    top: var(--joy-core-spacing-6);
    width: 40px;
    height: 40px;
    color: var(--joy-color-text-body);
    background-color: white;

    @media screen and (max-width: #{$_mobile-breakpoint}) {
      right: 5px;
      top: 5px;
    }

    &:after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      width: 100%;
      height: 100%;
      opacity: 0.05;
      background-color: transparent;
      border-radius: 50%;
      transition: background-color var(--joy-transition-duration-default) var(--joy-transition-timing-function);
    }

    &:hover, &:focus {
      &:after {
        background-color: currentColor;
      }
    }
  }

  &--footer {
    position: relative;
    display: flex;
    justify-content: flex-end;
    // Footer default style

    &-filled {
      margin-top: var(--joy-core-spacing-5);
    }

    @media screen and (min-width: #{$_mobile-cta_breakpoint + 1}) {
      joy-button:last-child {
        margin-left: var(--joy-core-spacing-4);
      }
    }

    @media screen and (max-width: #{$_mobile-cta_breakpoint}) {
      flex-direction: column;
      align-items: stretch;

      joy-button:last-child:not(:first-child) {
        margin-top: var(--joy-core-spacing-4);
      }
    }
  }
}

::slotted(p) {
  margin: 0;
  padding: 0;
}
